import { useState } from 'react'
import {
  Search,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  FileText,
  ChevronRight,
  Filter,
  BarChart3,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

interface TroubleshootingQuery {
  id: string
  query: string
  aircraft: string
  ataChapter: string
  timestamp: Date
  resolved: boolean
  resolutionTime?: string
  user: string
}

interface CommonIssue {
  ataChapter: string
  description: string
  occurrences: number
  avgResolutionTime: string
  trend: 'up' | 'down' | 'stable'
}

// Sample troubleshooting queries
const recentQueries: TroubleshootingQuery[] = [
  {
    id: '1',
    query: 'Hydraulic System A pressure fluctuation during taxi',
    aircraft: 'N11111 (B737-MAX)',
    ataChapter: 'ATA 29-10',
    timestamp: new Date(Date.now() - 1000 * 60 * 15),
    resolved: true,
    resolutionTime: '23 min',
    user: 'Tech. Rodriguez',
  },
  {
    id: '2',
    query: 'APU fails to start on second attempt',
    aircraft: 'N67890 (A320)',
    ataChapter: 'ATA 49-00',
    timestamp: new Date(Date.now() - 1000 * 60 * 45),
    resolved: true,
    resolutionTime: '18 min',
    user: 'Tech. Chen',
  },
  {
    id: '3',
    query: 'Pack valve cycling erratically in flight',
    aircraft: 'N67890 (A320)',
    ataChapter: 'ATA 21-51',
    timestamp: new Date(Date.now() - 1000 * 60 * 90),
    resolved: false,
    user: 'Tech. Williams',
  },
  {
    id: '4',
    query: 'Brake wear indicator showing early warning',
    aircraft: 'N12345 (B737-800)',
    ataChapter: 'ATA 32-42',
    timestamp: new Date(Date.now() - 1000 * 60 * 120),
    resolved: true,
    resolutionTime: '12 min',
    user: 'Tech. Martinez',
  },
  {
    id: '5',
    query: 'Engine oil quantity indication intermittent',
    aircraft: 'N12345 (B737-800)',
    ataChapter: 'ATA 79-00',
    timestamp: new Date(Date.now() - 1000 * 60 * 180),
    resolved: true,
    resolutionTime: '35 min',
    user: 'Tech. Johnson',
  },
]

// Common issues by ATA chapter
const commonIssues: CommonIssue[] = [
  {
    ataChapter: 'ATA 29 - Hydraulic Power',
    description: 'Pressure fluctuations, pump failures',
    occurrences: 24,
    avgResolutionTime: '28 min',
    trend: 'up',
  },
  {
    ataChapter: 'ATA 32 - Landing Gear',
    description: 'Brake wear, tire pressure, gear indication',
    occurrences: 18,
    avgResolutionTime: '15 min',
    trend: 'stable',
  },
  {
    ataChapter: 'ATA 49 - APU',
    description: 'Start failures, abnormal shutdowns',
    occurrences: 15,
    avgResolutionTime: '22 min',
    trend: 'down',
  },
  {
    ataChapter: 'ATA 21 - Air Conditioning',
    description: 'Pack valve issues, temperature control',
    occurrences: 12,
    avgResolutionTime: '25 min',
    trend: 'up',
  },
  {
    ataChapter: 'ATA 79 - Oil',
    description: 'Quantity indication, pressure warnings',
    occurrences: 9,
    avgResolutionTime: '20 min',
    trend: 'stable',
  },
]

// Chart data
const issuesByChapter = [
  { chapter: 'ATA 29', count: 24 },
  { chapter: 'ATA 32', count: 18 },
  { chapter: 'ATA 49', count: 15 },
  { chapter: 'ATA 21', count: 12 },
  { chapter: 'ATA 79', count: 9 },
  { chapter: 'ATA 24', count: 7 },
  { chapter: 'ATA 27', count: 5 },
]

const resolutionData = [
  { name: 'Resolved', value: 87 },
  { name: 'Pending', value: 8 },
  { name: 'Escalated', value: 5 },
]

const COLORS = ['#22c55e', '#eab308', '#ef4444']

// Trending issues
const trendingIssues = [
  {
    id: '1',
    issue: 'Hydraulic System A pressure sensor drift',
    aircraft: 'B737 Fleet',
    count: 8,
    trend: 'up',
    severity: 'high',
  },
  {
    id: '2',
    issue: 'APU bleed valve slow response',
    aircraft: 'A320 Fleet',
    count: 5,
    trend: 'up',
    severity: 'medium',
  },
  {
    id: '3',
    issue: 'TCAS intermittent warnings',
    aircraft: 'All Aircraft',
    count: 3,
    trend: 'stable',
    severity: 'low',
  },
]

export function TroubleshootingPage() {
  const [searchQuery, setSearchQuery] = useState('')

  const formatTime = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / (1000 * 60))

    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} hr ago`
    return date.toLocaleDateString()
  }

  const filteredQueries = recentQueries.filter(
    (q) =>
      q.query.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.ataChapter.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Troubleshooting Dashboard</h1>
          <p className="text-gray-600">Track issues, resolutions, and fleet-wide trends</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">
            <Filter className="mr-2 h-4 w-4" />
            Filters
          </Button>
          <Button>
            <FileText className="mr-2 h-4 w-4" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Queries Today</p>
                <p className="text-3xl font-bold">47</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                <Search className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <p className="mt-2 text-sm text-green-600">+12% from yesterday</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Resolution Rate</p>
                <p className="text-3xl font-bold">87%</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <p className="mt-2 text-sm text-green-600">+3% from last week</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Resolution</p>
                <p className="text-3xl font-bold">22m</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <p className="mt-2 text-sm text-green-600">-5 min from avg</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Open Issues</p>
                <p className="text-3xl font-bold">8</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <p className="mt-2 text-sm text-red-600">3 require attention</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent Queries */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Troubleshooting Queries</CardTitle>
                <CardDescription>Latest maintenance questions and resolutions</CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search queries..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredQueries.map((query) => (
                <div
                  key={query.id}
                  className="flex items-start justify-between rounded-lg border p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      {query.resolved ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <Clock className="h-4 w-4 text-yellow-500" />
                      )}
                      <span className="font-medium">{query.query}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span>{query.aircraft}</span>
                      <Badge variant="outline">{query.ataChapter}</Badge>
                      <span>{query.user}</span>
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-gray-500">{formatTime(query.timestamp)}</p>
                    {query.resolved && query.resolutionTime && (
                      <p className="text-green-600">Resolved in {query.resolutionTime}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Resolution Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Resolution Status</CardTitle>
            <CardDescription>This week's query outcomes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={resolutionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                  >
                    {resolutionData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {resolutionData.map((item, index) => (
                <div key={item.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: COLORS[index] }}
                    />
                    <span className="text-sm">{item.name}</span>
                  </div>
                  <span className="font-medium">{item.value}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Issues by ATA Chapter */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Issues by ATA Chapter
            </CardTitle>
            <CardDescription>Distribution of troubleshooting queries</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={issuesByChapter} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="chapter" type="category" width={60} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Trending Issues */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-500" />
              Trending Issues
            </CardTitle>
            <CardDescription>Problems appearing across multiple aircraft</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {trendingIssues.map((issue) => (
                <div key={issue.id} className="rounded-lg border p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <span className="font-medium">{issue.issue}</span>
                      <p className="text-sm text-gray-500">{issue.aircraft}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          issue.severity === 'high'
                            ? 'bg-red-100 text-red-800'
                            : issue.severity === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }
                      >
                        {issue.severity}
                      </Badge>
                      {issue.trend === 'up' && (
                        <TrendingUp className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm text-gray-500">{issue.count} occurrences this week</span>
                    <Button size="sm" variant="outline">
                      View Details
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Common Issues Table */}
      <Card>
        <CardHeader>
          <CardTitle>Common Issues by ATA Chapter</CardTitle>
          <CardDescription>Most frequent troubleshooting categories</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left text-sm text-gray-500">
                  <th className="pb-3 font-medium">ATA Chapter</th>
                  <th className="pb-3 font-medium">Common Issues</th>
                  <th className="pb-3 font-medium">Occurrences</th>
                  <th className="pb-3 font-medium">Avg Resolution</th>
                  <th className="pb-3 font-medium">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {commonIssues.map((issue) => (
                  <tr key={issue.ataChapter} className="hover:bg-gray-50">
                    <td className="py-3 font-medium">{issue.ataChapter}</td>
                    <td className="py-3 text-gray-600">{issue.description}</td>
                    <td className="py-3">
                      <Badge variant="secondary">{issue.occurrences}</Badge>
                    </td>
                    <td className="py-3">{issue.avgResolutionTime}</td>
                    <td className="py-3">
                      {issue.trend === 'up' && (
                        <span className="flex items-center text-red-600">
                          <TrendingUp className="mr-1 h-4 w-4" />
                          Increasing
                        </span>
                      )}
                      {issue.trend === 'down' && (
                        <span className="flex items-center text-green-600">
                          <TrendingUp className="mr-1 h-4 w-4 rotate-180" />
                          Decreasing
                        </span>
                      )}
                      {issue.trend === 'stable' && (
                        <span className="text-gray-500">Stable</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
